import type { DrawerProps } from '@mui/material';
import { Divider, Drawer, IconButton } from '@mui/material';

import { isPerUnitUsageProductOnly, isTieredSubscriptionUsageProductOnly, isTiersUsageProductOnly } from '@weft/services';
import type { UsageProductInput } from '@weft/types';
import { DEFAULT_CURRENCY } from '@weft/types';
import { raise, withDefault } from '@weft/utils';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CloseIcon } from '../../../../../components/icons';
import { ChargeProvider, useChargeContext } from '../charge-context';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { PerUnitChargeEditDrawerContent } from './usage-charge-edit-drawer-components/contents/PerUnitChargeEditDrawerContent';
import { TieredSubscriptionChargeEditDrawerContent } from './usage-charge-edit-drawer-components/contents/TieredSubscriptionChargeEditDrawer';
import { TiersPerUnitChargeEditDrawerContent } from './usage-charge-edit-drawer-components/contents/TiersChargeEditDrawerContent';

type FixedChargeEditDrawerProps = Pick<DrawerProps, 'open' | 'onClose'> & {
  usageProduct: UsageProductInput;
};

export const UsageChargeEditDrawer: React.FC<FixedChargeEditDrawerProps> = ({ usageProduct: _test, ...props }) => {
  const { item: usageProduct } = useChargeContext<UsageProductInput>();
  const { watch } = useFormContext<PlanInputForm, unknown, NewPlanForm>();

  const { name } = usageProduct.variants[0];
  const usageProducts = withDefault(watch('usageProducts'), []);
  const productIndex = usageProducts.findIndex((product) => product.id === usageProduct.id);

  const ChargeEditDrawerItem = useMemo(() => chargeEditDrawerItemFactory().create(usageProduct), [usageProduct]);

  const handleCloseClick = () => {
    props.onClose?.({}, 'backdropClick');
  };

  return (
    <Drawer
      anchor="right"
      PaperProps={{
        className: 'bg-transparent',
        elevation: 0,
      }}
      slotProps={{
        backdrop: {
          invisible: true,
        },
      }}
      {...props}
    >
      <div className="m-4 h-full min-w-[380px] max-w-[380px] rounded-lg border border-[#E7EEF3] bg-white shadow-[0px_8px_24px_0px_#00000014] overflow-y-auto">
        <div className="flex items-baseline justify-between p-4">
          <span>{name}</span>
          <IconButton
            onClick={handleCloseClick}
          >
            <CloseIcon
              color="#576063"
              width={20}
              height={20}
            />
          </IconButton>
        </div>

        <Divider/>

        <div className="p-6">
          <ChargeProvider value={{
            item: usageProduct,
            itemIndex: productIndex,
            editable: true,
            currency: DEFAULT_CURRENCY,
          }}
          >
            <ChargeEditDrawerItem/>
          </ChargeProvider>
        </div>
      </div>
    </Drawer>
  );
};

function chargeEditDrawerItemFactory(): { create: (product: UsageProductInput) => React.FC } {
  const create = (product: UsageProductInput): React.FC => {
    if (isTiersUsageProductOnly(product)) {
      return TiersPerUnitChargeEditDrawerContent;
    }

    if (isPerUnitUsageProductOnly(product)) {
      return PerUnitChargeEditDrawerContent;
    }

    if (isTieredSubscriptionUsageProductOnly(product)) {
      return TieredSubscriptionChargeEditDrawerContent;
    }

    return raise(`unknown product type ${JSON.stringify(product, null, 2)}`);
  };

  return {
    create,
  };
}